import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import {persistor} from "../../Redux/Store"
import logo from "../../../public/assets/images/icon.png"; // Adjust the path as necessary
const Header = ({  onSidebarToggle, sidebarCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  let user = JSON.parse(localStorage.getItem('user'));
 
  const navigationItems = [
    { 
      label: 'Dashboard', 
      path: user?.role === 'admin' ? '/admin-dashboard' : '/vendor-dashboard',
      icon: 'LayoutDashboard',
      roles: ['admin', 'vendor', ]
    },
    { 
      label: 'Products', 
      path: '/product-management',
      icon: 'Package',
      roles: ['vendor']
    },
    { 
      label: 'Employees', 
      path: '/employee-management',
      icon: 'Users',
      roles: [ 'vendor']
    },
    { 
      label: 'Settings', 
      path: '/user-profile-settings',
      icon: 'Settings',
      roles: ['admin', 'vendor', 'employee']
    }
  ];

  const visibleNavItems = navigationItems.filter(item => 
    item.roles.includes(user?.role)
  ).slice(0, 4);

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleProfileToggle = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
  };

  const handleLogout = () => {
     localStorage.removeItem('user');
        sessionStorage.removeItem('token');
        persistor.purge();
    navigate('/whats-app-authentication');
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'vendor': return 'Vendor Manager';
      case 'employee': return 'Employee';
      default: return 'User';
    }
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-1000">
        <div className="flex items-center justify-between h-full px-4">
          {/* Left Section - Logo and Navigation */}
          <div className="flex items-center space-x-6">
            {/* Sidebar Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onSidebarToggle}
              className="lg:hidden"
            >
              <Icon name="Menu" size={20} />
            </Button>

            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
                <img src={logo} name="Building2" size={20} color="white"  />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-foreground">Workly</h1>
                <p className="text-xs text-muted-foreground -mt-1">{JSON.parse(window.localStorage.getItem('user'))?.role} Panel</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {visibleNavItems.map((item) => (
                <Button
                  key={item.path}
                  variant={location.pathname === item.path ? "default" : "ghost"}
                  size="sm"
                  onClick={() => handleNavigation(item.path)}
                  iconName={item.icon}
                  iconPosition="left"
                  iconSize={16}
                  className="micro-animation"
                >
                  {item.label}
                </Button>
              ))}
            </nav>
          </div>

          {/* Right Section - User Profile */}
          <div className="flex items-center space-x-4">
            {/* User Info - Hidden on mobile */}
            <div className="hidden md:block text-right">
              <p className="text-sm font-medium text-foreground">
                {user?.name || 'User Name'}
              </p>
              <p className="text-xs text-muted-foreground">
                {getRoleDisplayName(user?.role)}
              </p>
            </div>

            {/* Profile Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleProfileToggle}
                className="flex items-center space-x-2 micro-animation"
              >
                <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} />
                </div>
                <Icon 
                  name="ChevronDown" 
                  size={14} 
                  className={`hidden sm:block transition-transform duration-200 ${
                    profileDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </Button>

              {/* Dropdown Menu */}
              {profileDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-md shadow-modal z-1200 animate-fade-in">
                  <div className="p-3 border-b border-border">
                    <p className="text-sm font-medium text-popover-foreground">
                      {user?.name || 'User Name'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {user?.email || 'user@example.com'}
                    </p>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => {
                        handleNavigation('/user-profile-settings');
                        setProfileDropdownOpen(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-popover-foreground hover:bg-muted micro-animation flex items-center space-x-2"
                    >
                      <Icon name="Settings" size={16} />
                      <span>Profile Settings</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full px-3 py-2 text-left text-sm text-destructive hover:bg-muted micro-animation flex items-center space-x-2"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-1100 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={handleMobileMenuToggle} />
          <div className="fixed top-0 left-0 h-full w-64 bg-card border-r border-border animate-slide-in">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
                    <Icon name="Building2" size={20} color="white" />
                  </div>
                  <div>
                    <h1 className="text-lg font-semibold text-foreground">VendorHub</h1>
                    <p className="text-xs text-muted-foreground -mt-1">Admin Panel</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleMobileMenuToggle}
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>
            </div>
            <nav className="p-4 space-y-2">
              {visibleNavItems.map((item) => (
                <Button
                  key={item.path}
                  variant={location.pathname === item.path ? "default" : "ghost"}
                  fullWidth
                  onClick={() => handleNavigation(item.path)}
                  iconName={item.icon}
                  iconPosition="left"
                  iconSize={16}
                  className="justify-start"
                >
                  {item.label}
                </Button>
              ))}
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;